import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Link from 'next/link';
import React, { useRef } from 'react';
import { useOutsideClick } from '../../../../../utils/hooks/useOutsideClick';
import { colors, spacing } from '../../../../../utils/styleguide';
import Icon from '../../../Icon';
import Typography from '../../../text/Typography';
import { NavItemType, NavLinkItemType } from '../../navTypes';
import MobileNavBarWrapper from '../MobileNavBarWrapper';

const BackIcon = styled(Icon)`
  margin: 0 ${spacing[2]}px;
`;

const SubmenuHeader = styled.button`
  display: flex;
  align-items: center;
  height: 32px;
  padding: ${spacing[1]}px 0;
  margin-top: -${spacing[1]}px;
  cursor: pointer;
  border: none;
  outline: none;
  background: none;
`;

const LinksContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  height: 100%;
`;

const LinkWrapper = css`
  width: 50%;
  height: fit-content;
  max-width: 166px;
  cursor: pointer;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  box-sizing: border-box;
  padding: ${spacing[3]}px ${spacing[2]}px;
  background: ${colors.white};

  &:hover {
    background: ${colors.yellow};
  }
`;

const LinkImageBlock = styled.div`
  overflow: hidden;
  img {
    height: 60px;
    width: 60px;
    object-fit: contain;
  }
`;

const LinkText = styled.div`
  margin-top: ${spacing[6]}px;
  display: flex;
  justify-content: center;
  width: 100%;
  text-decoration: none;
  outline: none;
  &:hover,
  &:active,
  &:focus {
    outline: none;
  }
`;

const DescriptionText = styled.div`
  margin-top: ${spacing[3]}px;
  display: flex;
  justify-content: center;
  width: 100%;
  text-decoration: none;
`;

const TitleBlock = css`
  text-align: center;
`;

type MobileSubmenuProps = {
  submenu: NavLinkItemType | null | undefined;
  isOpened: boolean;
  goBack: () => void;
  clickOutside: (event: MouseEvent) => void;
  onLinkedClicked: () => void;
};

const MobileSubmenu = ({
  submenu,
  isOpened,
  goBack,
  clickOutside,
  onLinkedClicked,
}: MobileSubmenuProps) => {
  const mobileSubmenuRef = useRef(null);
  useOutsideClick(mobileSubmenuRef, clickOutside);

  return (
    <MobileNavBarWrapper isOpened={!!submenu && isOpened} ref={mobileSubmenuRef}>
      <SubmenuHeader onClick={goBack} aria-label={'back'}>
        <BackIcon name="arrow-left-long" size={22} />
        <Typography variant="buttonLarge" tag="p">
          {submenu?.title}
        </Typography>
      </SubmenuHeader>
      {submenu?.links && (
        <LinksContainer>
          {submenu.links?.map(
            ({ title, image, href, richText, description }: NavItemType, index) => {
              return title ? (
                <Link href={href as string} key={index}>
                  <a
                    aria-label={title}
                    css={LinkWrapper}
                    href={href as string}
                    onClick={onLinkedClicked}
                  >
                    <LinkImageBlock>{image}</LinkImageBlock>
                    <LinkText>
                      <Typography
                        css={TitleBlock}
                        color={colors.black}
                        variant="buttonLarge"
                        tag="p"
                      >
                        {richText}
                      </Typography>
                    </LinkText>
                    <DescriptionText>
                      <Typography css={TitleBlock} color={colors.black} variant="body" tag="p">
                        {description}
                      </Typography>
                    </DescriptionText>
                  </a>
                </Link>
              ) : (
                <div key={index} css={LinkWrapper}>
                  <LinkImageBlock>{image}</LinkImageBlock>
                  <LinkText>
                    <Typography css={TitleBlock} color={colors.black} variant="buttonLarge" tag="p">
                      {richText}
                    </Typography>
                  </LinkText>
                  <DescriptionText>
                    <Typography css={TitleBlock} color={colors.black} variant="body" tag="p">
                      {description}
                    </Typography>
                  </DescriptionText>
                </div>
              );
            },
          )}
        </LinksContainer>
      )}
    </MobileNavBarWrapper>
  );
};

export default MobileSubmenu;
